import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import { v4 as uuidv4 } from 'uuid'
import * as constants from 'static/constants'
import ReactSelect from 'react-select'
import { Button, Toggle } from 'components/atoms'

interface ITourSkill {
  id?: number
  agent_tour_skill_id: number | undefined
  level: number | undefined
  disabled: boolean
}
interface IAgentTourSkill {
  id: number
  name: string
}
interface ITourSkillOption {
  value: string
  label: string
}
interface IProps {
  tourSkills: ITourSkill[]
  setTourSkills: React.Dispatch<React.SetStateAction<ITourSkill[]>>
  agentTourSkills: IAgentTourSkill[]
}

const { SKILL_LEVELS } = constants

const TourSkillSelect = ({ tourSkills, setTourSkills, agentTourSkills }: IProps) => {
  const agentTourSkillOptions = React.useCallback(
    (tourSkill: ITourSkill): ITourSkillOption[] => {
      const ids = tourSkills.map(item => item.agent_tour_skill_id)
      return agentTourSkills
        .filter(ats => tourSkill.agent_tour_skill_id === ats.id || !ids.includes(ats.id))
        .map(ats => ({
          value: String(ats.id),
          label: ats.name,
        }))
    },
    [agentTourSkills, tourSkills]
  )

  const handleDelete = React.useCallback(
    (index: number) => {
      setTourSkills(oldValues => oldValues.filter((_, i) => i !== index))
    },
    [tourSkills]
  )

  const handleAdd = React.useCallback(() => {
    setTourSkills(oldValues => [
      ...oldValues,
      {
        agent_tour_skill_id: undefined,
        level: 1,
        disabled: false,
      },
    ])
  }, [tourSkills])

  const handleUpdateTourSkill = React.useCallback(
    (index, params) => {
      setTourSkills(
        index === -1
          ? tourSkills
          : [
              ...tourSkills.slice(0, index),
              { ...tourSkills[index], ...params },
              ...tourSkills.slice(index + 1),
            ]
      )
    },
    [tourSkills]
  )

  return (
    <Wrapper>
      <p className="TourSkill_Title">{I18n.t('generic.tour_skill')}</p>
      <div className="TourSkills">
        {tourSkills.map((tourSkill, index) => {
          const options = agentTourSkillOptions(tourSkill)

          return (
            <div className="TourSkillItem" key={uuidv4()}>
              <div className="TourSkillItemInner">
                <ReactSelect
                  className="TourSkillSelect"
                  placeholder={I18n.t('generic.select_tour_skill')}
                  options={options}
                  isMulti={false}
                  onChange={e =>
                    handleUpdateTourSkill(index, { agent_tour_skill_id: Number(e.value) })
                  }
                  value={
                    options.find(
                      option => option.value === String(tourSkill.agent_tour_skill_id)
                    ) || null
                  }
                  menuPortalTarget={document.querySelector('body')}
                />
                <ReactSelect
                  className="LevelSelect"
                  placeholder={I18n.t('generic.select_tour_skill')}
                  options={Object.keys(constants.SKILL_LEVELS).map(key => ({
                    value: key,
                    label: constants.SKILL_LEVELS[key],
                  }))}
                  isMulti={false}
                  onChange={e => handleUpdateTourSkill(index, { level: e.value })}
                  defaultValue={{
                    value: tourSkill.level,
                    label: constants.SKILL_LEVELS[tourSkill.level],
                  }}
                  menuPortalTarget={document.querySelector('body')}
                />
                <Toggle
                  name="active"
                  checked={!tourSkill.disabled}
                  onChangeHandler={e =>
                    handleUpdateTourSkill(index, { disabled: !e.target.checked })
                  }
                  hideState={true}
                />
                <div className="toggleState">{tourSkill.disabled ? '無効' : '有効'}</div>
              </div>
              <Button
                small={true}
                textColor={constants.COLORS.white}
                backgroundColor={constants.COLORS.caution}
                handleClick={() => {
                  handleDelete(index)
                }}
              >
                {I18n.t('generic.delete')}
              </Button>
            </div>
          )
        })}
        <div className="addBtn">
          <Button
            small={true}
            handleClick={handleAdd}
            disabled={tourSkills.length === agentTourSkills.length}
          >
            + {I18n.t('generic.add')}
          </Button>
        </div>
      </div>
    </Wrapper>
  )
}

export default TourSkillSelect

const Wrapper = styled.div`
  display: flex;
  align-items: baseline;
  margin-top: 24px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }

  .TourSkills {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .TourSkill_Title {
    min-width: 180px;
    font-size: 14px;
    font-weight: bold;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      max-width: unset;
      margin-bottom: 12px;
    }
  }

  .TourSkillItem {
    display: flex;
    width: 100%;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    .TourSkillItemInner {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-grow: 1;
    }

    .TourSkillSelect {
      max-width: 400px;
      flex-grow: 1;
      font-size: 14px;
    }

    .LevelSelect {
      width: 100px;
    }
  }

  .addBtn {
    display: flex;
    justify-content: end;
    button {
      width: 100%;
    }
  }
`
